import { setup, assign, assertEvent, and } from 'xstate';
export interface GameNote {
  cellId: string;
  midiNote: number;
  startMs: number;
  durationMs: number;
}
export interface NoteGroup {
  cellIds: string[];
  midiNotes: number[];
  startMs: number;
}
export interface GameContext {
  noteGroups: NoteGroup[];
  currentGroupIndex: number;
  targetCellIds: string[];
  pressedCellIds: string[];
  pressedMidiNotes: number[];
  startTimeMs: number;
  finishTimeMs: number;
  error: string | null;
  tuningWarnAcknowledged: boolean;
}
export type GameEvent =
  | { type: 'FILE_DROPPED'; file: File }
  | { type: 'SONG_LOADED'; noteGroups: NoteGroup[] }
  | { type: 'LOAD_FAILED'; error: string }
  | { type: 'NOTE_PRESSED'; cellId: string; midiNote: number }
  | { type: 'GAME_RESET' }
  | { type: 'GAME_RESTART' }
  | { type: 'TUNING_WARN_ACK' };
export const gameMachine = setup({
  types: {
    context: {} as GameContext,
    events: {} as GameEvent,
  },
  actions: {
    assignSongLoaded: assign(({ event }) => {
      assertEvent(event, 'SONG_LOADED');
      const pressedCellIds: string[] = [];
      const pressedMidiNotes: number[] = [];
      return {
        noteGroups: event.noteGroups,
        currentGroupIndex: 0,
        targetCellIds: event.noteGroups[0]?.cellIds ?? [],
        pressedCellIds,
        pressedMidiNotes,
        startTimeMs: Date.now(),
        finishTimeMs: 0,
        error: null,
      };
    }),
    assignLoadFailed: assign(({ event }) => {
      assertEvent(event, 'LOAD_FAILED');
      return { error: event.error };
    }),
    accumulateNote: assign(({ context, event }) => {
      assertEvent(event, 'NOTE_PRESSED');
      const newCellIds = context.pressedCellIds.includes(event.cellId)
        ? context.pressedCellIds
        : [...context.pressedCellIds, event.cellId];
      const newMidiNotes = context.pressedMidiNotes.includes(event.midiNote)
        ? context.pressedMidiNotes
        : [...context.pressedMidiNotes, event.midiNote];
      return { pressedCellIds: newCellIds, pressedMidiNotes: newMidiNotes };
    }),
    advanceGroup: assign(({ context }) => {
      const nextIndex = context.currentGroupIndex + 1;
      const pressedCellIds: string[] = [];
      const pressedMidiNotes: number[] = [];
      return {
        currentGroupIndex: nextIndex,
        targetCellIds: context.noteGroups[nextIndex]?.cellIds ?? [],
        pressedCellIds,
        pressedMidiNotes,
      };
    }),
    setFinishTime: assign(() => ({ finishTimeMs: Date.now() })),
    resetGame: assign((): Partial<GameContext> => ({
      noteGroups: [],
      currentGroupIndex: 0,
      targetCellIds: [],
      pressedCellIds: [],
      pressedMidiNotes: [],
      startTimeMs: 0,
      finishTimeMs: 0,
      error: null,
      tuningWarnAcknowledged: false,
    })),
    restartGame: assign(({ context }) => {
      const pressedCellIds: string[] = [];
      const pressedMidiNotes: number[] = [];
      return {
        currentGroupIndex: 0,
        targetCellIds: context.noteGroups[0]?.cellIds ?? [],
        pressedCellIds,
        pressedMidiNotes,
        startTimeMs: Date.now(),
        finishTimeMs: 0,
      };
    }),
    ackTuningWarn: assign(() => ({ tuningWarnAcknowledged: true })),
  },
  guards: {
    isCorrectNote: ({ context, event }) => {
      assertEvent(event, 'NOTE_PRESSED');
      const group = context.noteGroups[context.currentGroupIndex];
      if (!group) return false;
      return group.cellIds.includes(event.cellId) || group.midiNotes.includes(event.midiNote);
    },
    isChordComplete: ({ context, event }) => {
      assertEvent(event, 'NOTE_PRESSED');
      const group = context.noteGroups[context.currentGroupIndex];
      if (!group) return false;
      const allCellIds = new Set([...context.pressedCellIds, event.cellId]);
      const allMidiNotes = new Set([...context.pressedMidiNotes, event.midiNote]);
      return group.cellIds.every((id, i) => {
        const midi = group.midiNotes[i];
        return allCellIds.has(id) || (midi !== undefined && allMidiNotes.has(midi));
      });
    },
    isLastGroup: ({ context }) =>
      context.currentGroupIndex + 1 >= context.noteGroups.length,
  },
}).createMachine({
  id: 'game',
  context: {
    noteGroups: [],
    currentGroupIndex: 0,
    targetCellIds: [],
    pressedCellIds: [],
    pressedMidiNotes: [],
    startTimeMs: 0,
    finishTimeMs: 0,
    error: null,
    tuningWarnAcknowledged: false,
  },
  initial: 'idle',
  states: {
    idle: {
      on: {
        FILE_DROPPED: 'loading',
      },
    },
    loading: {
      on: {
        SONG_LOADED: { target: 'playing', actions: 'assignSongLoaded' },
        LOAD_FAILED: { target: 'error', actions: 'assignLoadFailed' },
        GAME_RESET: { target: 'idle', actions: 'resetGame' },
      },
    },
    playing: {
      on: {
        NOTE_PRESSED: [
          {
            guard: and(['isCorrectNote', 'isChordComplete', 'isLastGroup']),
            target: 'complete',
            actions: ['accumulateNote', 'advanceGroup', 'setFinishTime'],
          },
          {
            guard: and(['isCorrectNote', 'isChordComplete']),
            actions: ['accumulateNote', 'advanceGroup'],
          },
          {
            guard: 'isCorrectNote',
            actions: 'accumulateNote',
          },
        ],
        GAME_RESET: { target: 'idle', actions: 'resetGame' },
        GAME_RESTART: { target: 'playing', actions: 'restartGame' },
        TUNING_WARN_ACK: { actions: 'ackTuningWarn' },
        FILE_DROPPED: { target: 'loading', actions: 'resetGame' },
      },
    },
    complete: {
      on: {
        GAME_RESET: { target: 'idle', actions: 'resetGame' },
        GAME_RESTART: { target: 'playing', actions: 'restartGame' },
        FILE_DROPPED: 'loading',
      },
    },
    error: {
      on: {
        GAME_RESET: { target: 'idle', actions: 'resetGame' },
        FILE_DROPPED: 'loading',
      },
    },
  },
});
